import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Truck, Warehouse } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { suppliersAPI } from '../services/api';
import SupplierComparator from './SupplierComparator';

// Admin: link a product to suppliers with their own stock / purchase price
const ProductSuppliersEditor = ({ links, warehouseStock, onChange, primaryId = null, onPrimaryChange = () => {} }) => {
  const [suppliers, setSuppliers] = useState([]);

  useEffect(() => {
    suppliersAPI.getAll().then(setSuppliers).catch(() => setSuppliers([]));
  }, []);

  const used = new Set(links.map(l => l.supplier_id));
  const free = suppliers.filter(s => s.active && !used.has(s.id));
  const update = (idx, key, value) => onChange(links.map((l, i) => (i === idx ? { ...l, [key]: value } : l)));
  const remove = (idx) => onChange(links.filter((_, i) => i !== idx));
  const add = () => free.length && onChange([...links, { supplier_id: free[0].id, supplier_name: free[0].name, supplier_ref: '', purchase_price: '', stock: 0, lead_time_days: free[0].lead_time_days }]);
  const supplierTotal = links.reduce((n, l) => n + (parseInt(l.stock, 10) || 0), 0);

  return (
    <div data-testid="product-suppliers-editor">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Truck size={18} className="text-orange-500" /> Fournisseurs & stock fournisseur</h3>
          <p className="text-xs text-gray-500">
            Stock entrepôt : <strong>{parseInt(warehouseStock, 10) || 0}</strong> · stock fournisseurs : <strong>{supplierTotal}</strong> · disponible client : <strong data-testid="product-available-total">{(parseInt(warehouseStock, 10) || 0) + supplierTotal}</strong>
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={add} disabled={!free.length} data-testid="product-supplier-add" title={!free.length ? 'Créez d\'abord un fournisseur dans l\'onglet Fournisseurs' : ''}>
          <Plus size={14} className="mr-1" /> Ajouter un fournisseur
        </Button>
      </div>
      {suppliers.length === 0 && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="product-suppliers-empty">
          Aucun fournisseur enregistré. Créez-en dans l'onglet <strong>Fournisseurs</strong> de l'administration.
        </p>
      )}
      {links.length > 0 && (
        <div className="space-y-2">
          {links.map((l, idx) => (
            <div key={l.supplier_id} className="grid grid-cols-12 gap-2 items-end bg-gray-50 border border-gray-200 rounded-lg p-3" data-testid={`product-supplier-row-${idx}`}>
              <div className="col-span-12 sm:col-span-4">
                <label className="block text-xs font-medium text-gray-600 mb-1">Fournisseur</label>
                <select
                  data-testid={`product-supplier-select-${idx}`}
                  value={l.supplier_id}
                  onChange={(e) => { const s = suppliers.find(x => x.id === e.target.value); onChange(links.map((x, i) => (i === idx ? { ...x, supplier_id: s.id, supplier_name: s.name, lead_time_days: s.lead_time_days } : x))); }}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                >
                  {suppliers.filter(s => s.id === l.supplier_id || !used.has(s.id)).map(s => <option key={s.id} value={s.id}>{s.name} (délai {s.lead_time_days} j)</option>)}
                </select>
              </div>
              <div className="col-span-6 sm:col-span-3">
                <label className="block text-xs font-medium text-gray-600 mb-1">Réf. fournisseur</label>
                <Input data-testid={`product-supplier-ref-${idx}`} value={l.supplier_ref || ''} onChange={(e) => update(idx, 'supplier_ref', e.target.value)} placeholder="Ex : BD-4521" />
              </div>
              <div className="col-span-6 sm:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Prix d'achat HT (€)</label>
                <Input data-testid={`product-supplier-price-${idx}`} type="number" min="0" step="0.01" value={l.purchase_price ?? ''} onChange={(e) => update(idx, 'purchase_price', e.target.value)} />
              </div>
              <div className="col-span-8 sm:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Stock fournisseur</label>
                <Input data-testid={`product-supplier-stock-${idx}`} type="number" min="0" step="1" value={l.stock} onChange={(e) => update(idx, 'stock', e.target.value)} />
              </div>
              <div className="col-span-4 sm:col-span-1 flex justify-end h-10 items-center">
                <button type="button" data-testid={`product-supplier-remove-${idx}`} onClick={() => remove(idx)} className="p-2 text-red-500 hover:bg-red-50 rounded"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {links.length > 0 && <SupplierComparator links={links} primaryId={primaryId} onPrimaryChange={onPrimaryChange} />}
      {links.length === 0 && suppliers.length > 0 && (
        <p className="text-xs text-gray-500 flex items-center gap-1"><Warehouse size={12} /> Aucun fournisseur lié : seul le stock entrepôt est vendable.</p>
      )}
    </div>
  );
};

export default ProductSuppliersEditor;
